/* =============================================================================
   NO HANDOUTS — ANIMATION SYSTEM
   Cinematic dark atmosphere + scroll reveals + load entrance +
   Lenis smooth scroll + magnetic buttons + hero parallax.
   Respects prefers-reduced-motion throughout.
   ============================================================================= */

(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─────────────────────────────────────────────────────────────────────────
     AMBIENT BACKGROUND
     Three slowly drifting radial gradient light sources on a canvas.
  ───────────────────────────────────────────────────────────────────────── */
  function AmbientBackground() {
    var canvas = document.getElementById('nh-ambient');
    if (!canvas || reducedMotion) return;

    var ctx = canvas.getContext('2d');
    var W, H, t = 0;

    var lights = [
      // warm amber — distant stadium light bottom-left
      { bx: 0.15, by: 0.82, ax: 0.16, ay: 0.10, fx: 0.00070, fy: 0.00050, r: 0.55, R: 200, G: 150, B: 60,  a: 0.08 },
      // cool blue-grey — depth from upper-right
      { bx: 0.88, by: 0.22, ax: 0.12, ay: 0.13, fx: 0.00050, fy: 0.00080, r: 0.42, R: 100, G: 120, B: 170, a: 0.05 },
      // very subtle warm centre — atmosphere fill
      { bx: 0.52, by: 0.55, ax: 0.09, ay: 0.09, fx: 0.00030, fy: 0.00035, r: 0.30, R: 210, G: 170, B: 80,  a: 0.03 },
    ];

    function resize() {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }

    function tick() {
      t += 1;
      ctx.clearRect(0, 0, W, H);

      for (var i = 0; i < lights.length; i++) {
        var l  = lights[i];
        var cx = (l.bx + Math.sin(t * l.fx) * l.ax) * W;
        var cy = (l.by + Math.cos(t * l.fy) * l.ay) * H;
        var rr = l.r * Math.max(W, H);

        var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rr);
        g.addColorStop(0,    'rgba(' + l.R + ',' + l.G + ',' + l.B + ',' + l.a + ')');
        g.addColorStop(0.45, 'rgba(' + l.R + ',' + l.G + ',' + l.B + ',' + (l.a * 0.22) + ')');
        g.addColorStop(1,    'rgba(' + l.R + ',' + l.G + ',' + l.B + ',0)');

        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }

      requestAnimationFrame(tick);
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });
    requestAnimationFrame(tick);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     LENIS SMOOTH SCROLL
     Buttery inertia scroll. Returns the lenis instance so parallax and
     other scroll listeners can hook into the same RAF loop.
  ───────────────────────────────────────────────────────────────────────── */
  function initLenis() {
    if (!window.Lenis || reducedMotion) return null;

    var lenis = new Lenis({
      duration: 0.85,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothTouch: false,
      touchMultiplier: 2,
      prevent: function (node) {
        return !!node.closest('.disclosure__list');
      },
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return lenis;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     MAGNETIC BUTTONS
     CTAs subtly pull toward the cursor — snappy on enter, spring on leave.
     Applied to all primary + secondary buttons.
  ───────────────────────────────────────────────────────────────────────── */
  function initMagneticButtons() {
    if (reducedMotion) return;

    var buttons = document.querySelectorAll('.button--primary, .button--secondary');
    var STRENGTH = 0.30;

    buttons.forEach(function (btn) {
      // Skip disabled / aria-disabled buttons
      if (btn.getAttribute('aria-disabled') === 'true') return;

      btn.addEventListener('mousemove', function (e) {
        var rect = btn.getBoundingClientRect();
        var cx   = rect.left + rect.width  / 2;
        var cy   = rect.top  + rect.height / 2;
        var dx   = (e.clientX - cx) * STRENGTH;
        var dy   = (e.clientY - cy) * STRENGTH;

        btn.style.transition = 'box-shadow 0.2s ease';
        btn.style.transform  = 'translate(' + dx + 'px, ' + dy + 'px)';
        btn.style.boxShadow  = '0 8px 28px rgba(240,236,236,0.10)';
      });

      btn.addEventListener('mouseleave', function () {
        btn.style.transition = 'transform 0.55s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease';
        btn.style.transform  = '';
        btn.style.boxShadow  = '';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     HERO PARALLAX
     The hero banner image drifts at 30% of scroll speed, creating depth
     between the image and the text layer above it. Only active while the
     banner is in the viewport.
  ───────────────────────────────────────────────────────────────────────── */
  function initHeroParallax(lenis) {
    if (reducedMotion || window.innerWidth < 750) return;

    var banner = document.querySelector('.banner');
    if (!banner) return;

    var imgs = banner.querySelectorAll('.banner__media img');
    if (!imgs.length) return;

    var bannerBottom = 0;

    function measure() {
      bannerBottom = banner.getBoundingClientRect().bottom + window.scrollY;
    }

    function updateParallax(scrollY) {
      if (scrollY > bannerBottom) return;
      var offset = scrollY * 0.22;
      imgs.forEach(function (img) {
        img.style.transform = 'scale(1.12) translateY(' + offset + 'px)';
      });
    }

    measure();
    window.addEventListener('resize', measure, { passive: true });

    if (lenis) {
      lenis.on('scroll', function (e) { updateParallax(e.scroll); });
    } else {
      window.addEventListener('scroll', function () {
        updateParallax(window.scrollY);
      }, { passive: true });
    }

    updateParallax(window.scrollY);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SCROLL REVEAL
     Adds .nh-reveal + stagger index to product grid items and section titles.
  ───────────────────────────────────────────────────────────────────────── */
  function ScrollReveal() {
    if (reducedMotion) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.10, rootMargin: '0px 0px -32px 0px' });

    var grids = document.querySelectorAll(
      '.featured-collection .product-grid, .collection .product-grid, .product-recommendations .product-grid'
    );
    grids.forEach(function (grid) {
      var items = grid.querySelectorAll('.grid__item');
      items.forEach(function (item, idx) {
        item.classList.add('nh-reveal');
        item.style.setProperty('--nh-i', Math.min(idx, 5));
        observer.observe(item);
      });
    });

    document.querySelectorAll('.multicolumn-list__item').forEach(function (el, idx) {
      el.classList.add('nh-reveal');
      el.style.setProperty('--nh-i', idx % 4);
      observer.observe(el);
    });

    document.querySelectorAll('.rich-text__blocks, .collage__item').forEach(function (el, idx) {
      el.classList.add('nh-reveal');
      el.style.setProperty('--nh-i', idx % 3);
      observer.observe(el);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     LOAD ENTRANCE
  ───────────────────────────────────────────────────────────────────────── */
  function LoadEntrance() {
    if (reducedMotion) {
      document.documentElement.classList.add('nh-loaded');
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.add('nh-loaded');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     TICKER DUPLICATION
  ───────────────────────────────────────────────────────────────────────── */
  function initTicker() {
    var track = document.querySelector('.nh-ticker-track');
    if (!track) return;

    var originals = Array.prototype.slice.call(track.children);
    originals.forEach(function (child) {
      var clone = child.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    AmbientBackground();
    // Lenis smooth scroll disabled — native scroll for zero input lag,
    // especially on Windows mouse wheels. Parallax falls back to window scroll.
    var lenis = null;
    initHeroParallax(lenis);
    initMagneticButtons();
    ScrollReveal();
    LoadEntrance();
    initTicker();
  });

})();

/* ── Product gallery thumbnails ─────────────────────────────────────────
   Builds a thumbnail strip from the slides already in the DOM and scrolls
   the existing Dawn slider. No new gallery, no duplicate markup.        */
(function () {
  function initThumbs() {
    var list = document.querySelector('.product__media-list');
    if (!list || document.querySelector('.nh-thumbs')) return;
    var items = Array.prototype.filter.call(
      list.querySelectorAll('.product__media-item'),
      function (li) { return getComputedStyle(li).display !== 'none'; }
    );
    if (items.length < 2) return;

    var bar = document.createElement('div');
    bar.className = 'nh-thumbs';

    items.forEach(function (li, i) {
      var img = li.querySelector('img');
      if (!img) return;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'nh-thumb' + (i === 0 ? ' is-active' : '');
      b.setAttribute('aria-label', 'View image ' + (i + 1));
      var t = document.createElement('img');
      t.src = img.currentSrc || img.src;
      t.alt = '';
      t.loading = 'lazy';
      b.appendChild(t);
      b.addEventListener('click', function () {
        list.scrollTo({ left: li.offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
      });
      bar.appendChild(b);
    });

    list.parentNode.appendChild(bar);

    var tick;
    list.addEventListener('scroll', function () {
      clearTimeout(tick);
      tick = setTimeout(function () {
        var idx = Math.round(list.scrollLeft / list.clientWidth);
        var btns = bar.querySelectorAll('.nh-thumb');
        for (var i = 0; i < btns.length; i++) btns[i].classList.toggle('is-active', i === idx);
      }, 60);
    }, { passive: true });
  }
  if (document.readyState !== 'loading') initThumbs();
  else document.addEventListener('DOMContentLoaded', initThumbs);
})();
